import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { VscFeedback, VscReport, VscAccount, VscStarEmpty, VscStarFull, VscCalendar, VscClose, VscEye, VscChevronUp, VscChevronDown, VscSearch, VscClearAll, VscFilter, VscThumbsup, VscThumbsdown, VscComment, VscCircle, VscPass, VscLoading, VscError } from 'react-icons/vsc';
import { TbReportSearch } from 'react-icons/tb';
import { feedbackService, FeedbackResponse, AdminFeedbackFilters } from '../services/feedbackService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { ADMIN_CONFIG } from '../config/env';
import { useNavigate } from 'react-router-dom';
import { backendHistoricalReportService } from '../services/historicalReportService.backend';
import { httpClient } from '../services/httpClient';
import { apiPath } from '../config/env';
import AdminFailedJobsTab from './AdminFailedJobsTab';

interface AdminPanelProps { }

type SortOption = 'date-newest' | 'date-oldest' | 'rating-highest' | 'rating-lowest';
type AdminTab = 'feedback' | 'reports' | 'failed-jobs';

const AdminPanel: React.FC<AdminPanelProps> = () => {
    const { t } = useTranslate();
    const { user } = useAuth();
    const { push: pushToast } = useToast();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<AdminTab>('feedback');
    const [feedbackData, setFeedbackData] = useState<FeedbackResponse[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true); // Only for initial load
    const [isFiltering, setIsFiltering] = useState(false); // For filter changes
    const [error, setError] = useState<string | null>(null);
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackResponse | null>(null);
    const [filterType, setFilterType] = useState<'all' | 'general' | 'reports'>('all');
    const [sortBy, setSortBy] = useState<SortOption>('date-newest');

    // Enhanced filter state
    const [filters, setFilters] = useState<AdminFeedbackFilters>({
        limit: 50,
        offset: 0,
        order_by: 'created_at',
        order_direction: 'desc'
    });
    const [userEmailSearch, setUserEmailSearch] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Build filters object for API call (without user email - will filter client-side)
    const buildFilters = (): AdminFeedbackFilters => {
        const apiFilters: AdminFeedbackFilters = {
            limit: 500, // Get more data for client-side filtering
            offset: 0,
            order_by: 'created_at',
            order_direction: 'desc'
        };

        // Don't use user_email in API filters - we'll filter client-side for partial matching
        if (fromDate) {
            apiFilters.from_date = fromDate;
        }
        if (toDate) {
            apiFilters.to_date = toDate;
        }

        return apiFilters;
    };

    // Apply filters and search
    const applyFilters = async (isInitialLoad = false) => {
        if (!isAdmin) return;

        // Use different loading states for initial vs filter changes
        if (isInitialLoad) {
            setIsLoading(true);
        } else {
            setIsFiltering(true);
        }

        try {
            const apiFilters = buildFilters();
            const result = await feedbackService.getAdminFeedback(apiFilters);
            setFeedbackData(result.feedback);
            setTotalCount(result.total_count);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('Failed to apply filters');
            setError(errorMessage);
            pushToast({
                variant: 'error',
                message: errorMessage
            });
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            } else {
                setIsFiltering(false);
            }
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        setUserEmailSearch('');
        setFromDate('');
        setToDate('');
        setFilterType('all');
        setSortBy('date-newest');
    };

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Get date 30 days ago in YYYY-MM-DD format
    const getThirtyDaysAgo = () => {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return thirtyDaysAgo.toISOString().split('T')[0];
    };

    // Check if current user is admin - group-based access only
    const isAdmin = useMemo(() => {
        if (!user?.email) return false;

        const userGroups = user?.groupNamesCommaSeparated || '';

        // Check AD group membership (exact match)
        if (ADMIN_CONFIG.adminGroupName && userGroups.length > 0) {
            const groupList = userGroups.split(',').map(g => g.trim());
            return groupList.includes(ADMIN_CONFIG.adminGroupName);
        }

        return false;
    }, [user?.email, user?.groupNamesCommaSeparated]);

    // Open report in new tab using related_entity_id (the actual report ID)
    const handleViewReport = (feedback: FeedbackResponse) => {
        const params = new URLSearchParams({ reportId: feedback.related_entity_id! });
        if (feedback.user_email) params.set('userEmail', feedback.user_email);
        window.open(`/report?${params.toString()}`, '_blank');
    };

    // Handle escape key for modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && selectedFeedback) {
                setSelectedFeedback(null);
            }
        };

        if (selectedFeedback) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [selectedFeedback]);

    // Filter and sort feedback based on selected type and sort option
    const filteredFeedback = useMemo(() => {
        let filtered = feedbackData;

        // Apply client-side user email search (partial matching)
        if (userEmailSearch.trim()) {
            const searchTerm = userEmailSearch.trim().toLowerCase();
            filtered = filtered.filter(f =>
                f.user_email.toLowerCase().includes(searchTerm)
            );
        }

        // Apply filter type
        if (filterType === 'general') {
            filtered = filtered.filter(f => !f.related_entity_id);
        } else if (filterType === 'reports') {
            filtered = filtered.filter(f => f.related_entity_id);
        }

        // Apply sort
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'date-newest':
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'date-oldest':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'rating-highest':
                    const ratingA = a.feedback?.rating || 0;
                    const ratingB = b.feedback?.rating || 0;
                    return ratingB - ratingA;
                case 'rating-lowest':
                    const ratingA2 = a.feedback?.rating || 0;
                    const ratingB2 = b.feedback?.rating || 0;
                    return ratingA2 - ratingB2;
                default:
                    return 0;
            }
        });

        return sorted;
    }, [feedbackData, filterType, sortBy, userEmailSearch]);

    // Load feedback data with enhanced filtering
    useEffect(() => {
        if (!isAdmin) {
            setError(t('Access denied. You are not authorized to view the admin panel.'));
            setIsLoading(false);
            return;
        }

        applyFilters(true); // Initial load with full spinner
    }, [isAdmin]);

    // Apply filters when filter values change (with debounce)
    useEffect(() => {
        if (!isAdmin) return;

        const timeoutId = setTimeout(() => {
            applyFilters(false); // Filter change with subtle indicator
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [userEmailSearch, fromDate, toDate]);

    // Render star rating
    const renderStarRating = (rating: number) => {
        return (
            <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="text-yellow-500">
                        {star <= rating ? <VscStarFull size={16} /> : <VscStarEmpty size={16} />}
                    </span>
                ))}
            </div>
        );
    };

    // Format date only
    const formatDate = (timestamp: string) => {
        try {
            return new Date(timestamp).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return timestamp;
        }
    };

    // Format time only
    const formatTime = (timestamp: string) => {
        try {
            return new Date(timestamp).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return '';
        }
    };

    // Access denied screen
    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-red-200 p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <VscAccount className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('Access Denied')}</h2>
                    <p className="text-gray-600 mb-6">
                        {t('You are not authorized to access the admin panel. Please contact the administrator if you believe this is an error.')}
                    </p>
                    <div className="text-sm text-gray-500">
                        {t('Current user:')} {user?.email || t('Not logged in')}
                    </div>
                </div>
            </div>
        );
    }

    // Loading screen
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700">{t('Loading feedback data...')}</p>
                </div>
            </div>
        );
    }

    // Error screen
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-red-200 p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <VscClose className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('Error Loading Data')}</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t('Retry')}
                    </button>
                </div>
            </div>
        );
    }

    // Main admin panel
    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 p-6 notranslate">
            <div className="max-w-7xl mx-auto">
                {/* Floating Back Button */}
                <div className="fixed top-24 left-20 z-50">
                    <button
                        onClick={() => navigate('/datasources')}
                        className="inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full transition-all duration-200"
                        style={{
                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                            boxShadow: '0 2px 8px -2px rgba(220, 38, 38, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.015)';
                            e.currentTarget.style.boxShadow = '0 4px 12px -2px rgba(220, 38, 38, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = '';
                            e.currentTarget.style.boxShadow = '0 2px 8px -2px rgba(220, 38, 38, 0.25)';
                        }}
                        aria-label="Return to data sources"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>{t('Back')}</span>
                    </button>
                </div>

                {/* Header */}
                <div className="relative rounded-2xl shadow-lg border border-red-200 p-6 mb-6 overflow-hidden" style={{ background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)' }}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {activeTab === 'feedback' ? (
                                <VscFeedback className="w-7 h-7 text-white" />
                            ) : (
                                <TbReportSearch className="w-7 h-7 text-white" />
                            )}
                            <div>
                                <h1 className="text-xl font-bold text-white">
                                    {activeTab === 'feedback' ? t('Feedback Admin Panel') : t('Reports Admin Panel')}
                                </h1>
                                <p className="text-white/70 text-xs">
                                    {activeTab === 'feedback'
                                        ? t('Manage and review user feedback')
                                        : t('View and analyze user reports')
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-5">
                            {/* Tab Navigation - Slider */}
                            <div className="relative flex bg-white/15 backdrop-blur-sm rounded-full p-1 border border-white/10 w-[340px]">
                                {/* Sliding indicator */}
                                <div
                                    className="absolute top-1 bottom-1 w-[calc(33.33%-4px)] bg-white rounded-full shadow-sm transition-all duration-300 ease-in-out"
                                    style={{ left: activeTab === 'feedback' ? '4px' : activeTab === 'reports' ? 'calc(33.33% + 0px)' : 'calc(66.66% - 2px)' }}
                                />
                                <button
                                    onClick={() => setActiveTab('feedback')}
                                    className={`relative z-10 flex items-center justify-center w-1/3 py-1.5 rounded-full font-medium text-sm transition-colors duration-300 ${activeTab === 'feedback'
                                        ? 'text-brand-700'
                                        : 'text-white/80 hover:text-white'
                                        }`}
                                >
                                    <VscFeedback className="w-4 h-4 mr-1.5" />
                                    {t('Feedback')}
                                </button>
                                <button
                                    onClick={() => setActiveTab('reports')}
                                    className={`relative z-10 flex items-center justify-center w-1/3 py-1.5 rounded-full font-medium text-sm transition-colors duration-300 ${activeTab === 'reports'
                                        ? 'text-brand-700'
                                        : 'text-white/80 hover:text-white'
                                        }`}
                                >
                                    <TbReportSearch className="w-4 h-4 mr-1.5" />
                                    {t('Reports')}
                                </button>
                                <button
                                    onClick={() => setActiveTab('failed-jobs')}
                                    className={`relative z-10 flex items-center justify-center w-1/3 py-1.5 rounded-full font-medium text-sm transition-colors duration-300 ${activeTab === 'failed-jobs'
                                        ? 'text-brand-700'
                                        : 'text-white/80 hover:text-white'
                                        }`}
                                >
                                    <VscError className="w-4 h-4 mr-1.5" />
                                    {t('Failed Jobs')}
                                </button>
                            </div>
                            <div className="text-right hidden sm:block">
                                <p className="font-medium text-white text-sm">{user?.name}</p>
                                <p className="text-[11px] text-white/60">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'feedback' && (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                            <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <VscFeedback className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                                        <p className="text-sm text-gray-600">{t('Total Feedback')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <TbReportSearch className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-2xl font-bold text-gray-900">{feedbackData.filter(f => f.related_entity_id).length}</p>
                                        <p className="text-sm text-gray-600">{t('Report Feedback')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <VscAccount className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-2xl font-bold text-gray-900">{feedbackData.filter(f => !f.related_entity_id).length}</p>
                                        <p className="text-sm text-gray-600">{t('General Feedback')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                        <VscStarFull className="w-5 h-5 text-yellow-600" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-2xl font-bold text-gray-900">
                                            {feedbackData.length > 0
                                                ? (feedbackData.reduce((sum, f) => sum + (f.feedback?.rating || 0), 0) / feedbackData.length).toFixed(1)
                                                : '0.0'
                                            }
                                        </p>
                                        <p className="text-sm text-gray-600">{t('Filtered Avg Rating')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Filters */}
                        <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-6 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-4">
                                    <h2 className="text-xl font-bold text-gray-900">{t('Feedback Entries')}</h2>
                                    <span className="text-sm text-gray-500">({totalCount} total)</span>
                                    {/* Subtle filtering indicator */}
                                    {isFiltering && (
                                        <div className="flex items-center space-x-2 text-sm text-blue-600">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            <span>{t('Filtering...')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Basic Filter Buttons */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                {[
                                    { key: 'all', label: t('All Feedback'), count: feedbackData.length },
                                    { key: 'general', label: t('General'), count: feedbackData.filter(f => !f.related_entity_id).length },
                                    { key: 'reports', label: t('Reports'), count: feedbackData.filter(f => f.related_entity_id).length }
                                ].map(filter => (
                                    <button
                                        key={filter.key}
                                        onClick={() => setFilterType(filter.key as any)}
                                        className={`px-4 py-2 rounded-full border transition-all duration-200 font-semibold text-sm ${filterType === filter.key
                                            ? 'text-white border-transparent'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                                            }`}
                                        style={filterType === filter.key ? {
                                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                            boxShadow: '0 2px 8px -2px rgba(220, 38, 38, 0.25)'
                                        } : undefined}
                                    >
                                        {filter.label} ({filter.count})
                                    </button>
                                ))}
                            </div>

                            {/* Advanced Filters Panel */}
                            <div className="border-t pt-4 space-y-4">
                                {/* User Email Search */}
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <VscAccount className="inline w-4 h-4 mr-1" />
                                            {t('Search User Email')}
                                        </label>
                                        <input
                                            type="email"
                                            value={userEmailSearch}
                                            onChange={(e) => setUserEmailSearch(e.target.value)}
                                            placeholder="Enter user email..."
                                            disabled={isFiltering}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-gray-900 bg-white ${isFiltering ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                    </div>
                                </div>

                                {/* Date Range Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* From Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <VscCalendar className="inline w-4 h-4 mr-1" />
                                            {t('From Date')}
                                        </label>
                                        <input
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                            max={getTodayDate()}
                                            disabled={isFiltering}
                                            className={`date-us w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-gray-900 bg-white ${isFiltering ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                    </div>

                                    {/* To Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <VscCalendar className="inline w-4 h-4 mr-1" />
                                            {t('To Date')}
                                        </label>
                                        <input
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                            min={fromDate || getThirtyDaysAgo()}
                                            max={getTodayDate()}
                                            disabled={isFiltering}
                                            className={`date-us w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-gray-900 bg-white ${isFiltering ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feedback Table */}
                        <div className="bg-white rounded-2xl shadow-xl border border-red-200 overflow-hidden">
                            {filteredFeedback.length === 0 ? (
                                <div className="p-12 text-center">
                                    <VscFeedback className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-xl text-gray-500">{t('No feedback available')}</p>
                                    <p className="text-gray-400">{t('Feedback will appear here when users submit their responses.')}</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">{t('User')}</th>
                                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                                                    <button
                                                        onClick={() => {
                                                            setSortBy(sortBy === 'rating-highest' ? 'rating-lowest' : 'rating-highest');
                                                        }}
                                                        className="flex items-center justify-center space-x-1 hover:text-red-600 transition-colors w-full"
                                                    >
                                                        <span>{t('Rating')}</span>
                                                        {sortBy === 'rating-highest' ? (
                                                            <VscChevronUp className="w-4 h-4" />
                                                        ) : sortBy === 'rating-lowest' ? (
                                                            <VscChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <VscChevronDown className="w-4 h-4 opacity-30" />
                                                        )}
                                                    </button>
                                                </th>
                                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">{t('Comment')}</th>
                                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                                                    <button
                                                        onClick={() => {
                                                            setSortBy(sortBy === 'date-newest' ? 'date-oldest' : 'date-newest');
                                                        }}
                                                        className="flex items-center justify-center space-x-1 hover:text-red-600 transition-colors w-full"
                                                    >
                                                        <span>{t('Date')}</span>
                                                        {sortBy === 'date-newest' ? (
                                                            <VscChevronUp className="w-4 h-4" />
                                                        ) : sortBy === 'date-oldest' ? (
                                                            <VscChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <VscChevronDown className="w-4 h-4 opacity-30" />
                                                        )}
                                                    </button>
                                                </th>
                                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">{t('Actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredFeedback.map((feedback, index) => (
                                                <tr key={feedback.id || index} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                                                <VscAccount className="w-4 h-4 text-red-600" />
                                                            </div>
                                                            <div className="ml-3">
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {feedback.user_email}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {feedback.feedback?.rating ? (
                                                            renderStarRating(feedback.feedback.rating)
                                                        ) : (
                                                            <span className="text-sm text-gray-400">{t('No rating')}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 max-w-sm">
                                                        <p className="text-sm text-gray-900 line-clamp-2" title={feedback.feedback?.comment || t('No comment')}>
                                                            {feedback.feedback?.comment || t('No comment provided')}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        <div>
                                                            {formatDate(feedback.created_at)}
                                                        </div>
                                                        <div className="text-xs text-gray-600 mt-1">
                                                            {formatTime(feedback.created_at)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => setSelectedFeedback(feedback)}
                                                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                                                        >
                                                            {t('View Details')}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === 'reports' && (
                    <AdminReportsTab />
                )}

                {activeTab === 'failed-jobs' && (
                    <AdminFailedJobsTab />
                )}
            </div>

            {/* Feedback Detail Modal */}
            {activeTab === 'feedback' && selectedFeedback && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-[60000] flex items-center justify-center p-2 sm:p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-3xl w-full h-[90vh] sm:h-[80vh] flex flex-col border border-red-200">
                        {/* Modal Header */}
                        <div className="p-3 sm:p-4 md:p-6 border-b border-red-200 bg-gradient-to-r from-red-600 to-red-700 rounded-t-xl flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center shadow-sm backdrop-blur-sm">
                                        <VscFeedback className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-bold text-white">{t('Feedback Details')}</h3>
                                </div>
                                <div className="flex items-center space-x-3">
                                    {/* Related Report in Header Corner */}
                                    {(selectedFeedback.related_entity_id || selectedFeedback.report_id) && (
                                        <button
                                            onClick={() => handleViewReport(selectedFeedback)}
                                            className="hidden sm:flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-red-600 bg-white hover:bg-red-50 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-white"
                                        >
                                            <VscEye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                            <span className="hidden sm:inline">{t('View Report')}</span>
                                            <span className="sm:hidden">{t('Report')}</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setSelectedFeedback(null)}
                                        className="p-1 sm:p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors text-white backdrop-blur-sm"
                                        aria-label="Close feedback details modal"
                                    >
                                        <VscClose className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 bg-gradient-to-br from-red-50 via-white to-red-25">
                            <div className="space-y-6">
                                {/* User and Rating Side by Side */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* User Email */}
                                    <div className="bg-white rounded-xl p-5 shadow-lg border border-red-100 hover:shadow-xl transition-shadow">
                                        <label className="text-sm font-bold text-red-700 uppercase tracking-wider mb-2 block">{t('User')}</label>
                                        <p className="text-lg text-gray-900 font-semibold">{selectedFeedback.user_email}</p>
                                        <div className="mt-2 text-xs text-gray-500">
                                            {formatDate(selectedFeedback.created_at)} at {formatTime(selectedFeedback.created_at)}
                                        </div>
                                    </div>

                                    {/* Rating */}
                                    <div className="bg-white rounded-xl p-5 shadow-lg border border-red-100 hover:shadow-xl transition-shadow">
                                        <label className="text-sm font-bold text-red-700 uppercase tracking-wider mb-2 block">{t('Rating')}</label>
                                        {selectedFeedback.feedback?.rating ? (
                                            <div className="flex items-center space-x-2">
                                                {renderStarRating(selectedFeedback.feedback.rating)}
                                                <span className="text-lg font-semibold text-gray-900 ml-2">
                                                    {selectedFeedback.feedback.rating}/5
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-500 italic">{t('No rating provided')}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Comment - Main Focus */}
                                {selectedFeedback.feedback?.comment && (
                                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200 hover:shadow-xl transition-shadow">
                                        <label className="text-base font-bold text-red-700 uppercase tracking-wider mb-4 block flex items-center">
                                            <VscComment className="w-4 h-4 mr-2" />
                                            {t('Comment')}
                                        </label>
                                        <div className="bg-gradient-to-br from-red-25 to-red-50 border-2 border-red-100 rounded-xl p-6 min-h-[200px] max-h-[400px] overflow-y-auto">
                                            <p className="text-gray-900 whitespace-pre-wrap leading-relaxed text-base">
                                                {selectedFeedback.feedback.comment}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {!selectedFeedback.feedback?.comment && (
                                    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                                        <label className="text-base font-bold text-gray-500 uppercase tracking-wider mb-4 block flex items-center">
                                            <VscComment className="w-4 h-4 mr-2" />
                                            {t('Comment')}
                                        </label>
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 min-h-[100px] flex items-center justify-center">
                                            <p className="text-gray-500 italic text-center">{t('No comment provided')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Admin Reports Tab Component ─────────────────────────────────────────────
const AdminReportsTab: React.FC = () => {
    const { t } = useTranslate();
    const navigate = useNavigate();
    const { push: pushToast } = useToast();
    const [userSearch, setUserSearch] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [reports, setReports] = useState<Array<{ id: string; dataset: string; healthScore: number | null; issueCount: number | null; createdAt: string; userEmail: string }>>([]);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;

    // Feedback data keyed by report ID
    const [feedbackMap, setFeedbackMap] = useState<Record<string, { rating: number | null; comment: string | null }>>({});

    // Report search (filters by dataset name)
    const [reportSearch, setReportSearch] = useState<string>('');
    // User emails from /api/v1/users
    const [allUsers, setAllUsers] = useState<string[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // User dropdown state
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const userDropdownRef = useRef<HTMLDivElement>(null);



    // Fetch users on mount
    useEffect(() => {
        let active = true;
        const fetchUsers = async () => {
            setIsLoadingUsers(true);
            try {
                const result = await httpClient.get<any>(apiPath('users'));
                if (active && result.success) {
                    const data = result.data;
                    let items: any[] = [];
                    if (Array.isArray(data)) items = data;
                    else if (Array.isArray(data?.users)) items = data.users;
                    else if (Array.isArray(data?.emails)) items = data.emails;

                    const emails: string[] = items
                        .map((item: any) => (typeof item === 'string' ? item : item?.email))
                        .filter((e: any): e is string => typeof e === 'string' && e.length > 0);
                    setAllUsers(emails);
                }
            } catch {
                // silently fail
            } finally {
                if (active) setIsLoadingUsers(false);
            }
        };
        fetchUsers();
        return () => { active = false; };
    }, []);

    // Close user dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtered user list for dropdown
    const filteredUsers = useMemo(() => {
        if (!userSearch.trim()) return allUsers;
        const term = userSearch.trim().toLowerCase();
        return allUsers.filter(email => email.toLowerCase().includes(term));
    }, [allUsers, userSearch]);

    // Map API response to table rows
    const mapReports = (reportsList: any[], email: string) => {
        return reportsList.map(r => {
            let hs: number | null = null;
            if (typeof r.health_score_overall === 'number') hs = r.health_score_overall;
            else if (typeof r.health_score_overall === 'string') {
                const parsed = parseFloat(r.health_score_overall.replace(/%$/, ''));
                if (!Number.isNaN(parsed)) hs = parsed;
            }
            return {
                id: r.id,
                dataset: r.data_object_name || 'Unknown',
                healthScore: hs,
                issueCount: r.issue_count,
                createdAt: r.generated_at,
                userEmail: r.user_email || email,
            };
        });
    };

    // Auto-fetch reports for ALL users once user list is loaded
    useEffect(() => {
        if (allUsers.length === 0) return;
        let active = true;

        const fetchAllReports = async () => {
            setIsLoadingReports(true);
            setFeedbackMap({});
            const combined: typeof reports = [];

            // Fetch reports for each user in parallel (batched to avoid overwhelming)
            const CONCURRENCY = 5;
            for (let i = 0; i < allUsers.length; i += CONCURRENCY) {
                const batch = allUsers.slice(i, i + CONCURRENCY);
                const results = await Promise.allSettled(
                    batch.map(async (email) => {
                        const allReports: any[] = [];
                        let offset = 0;
                        const BATCH = 100;
                        let hasMore = true;

                        while (hasMore) {
                            const response = await backendHistoricalReportService.listReports({
                                user_email: email,
                                status: 'analyzed',
                                order_by: 'generated_at',
                                order_direction: 'desc',
                                limit: BATCH,
                                offset,
                            });
                            allReports.push(...response.reports);
                            offset += response.reports.length;
                            const total = response.total_count ?? offset;
                            hasMore = response.reports.length === BATCH && offset < total;
                        }
                        return mapReports(allReports, email);
                    })
                );

                if (!active) return;
                for (const result of results) {
                    if (result.status === 'fulfilled') {
                        combined.push(...result.value);
                    }
                }
            }

            if (active) {
                setReports(combined);
                setIsLoadingReports(false);
            }

            // Fetch feedback in background
            try {
                const fbResult = await feedbackService.getAdminFeedback({
                    limit: 500,
                    offset: 0,
                    order_by: 'created_at',
                    order_direction: 'desc',
                });
                if (active) {
                    const map: Record<string, { rating: number | null; comment: string | null }> = {};
                    for (const fb of fbResult.feedback) {
                        if (fb.related_entity_id) {
                            map[fb.related_entity_id] = {
                                rating: fb.feedback?.rating ?? null,
                                comment: fb.feedback?.comment ?? null,
                            };
                        }
                    }
                    setFeedbackMap(map);
                }
            } catch {
                // non-critical
            }
        };

        fetchAllReports();
        return () => { active = false; };
    }, [allUsers]);

    // Filter and group reports
    const filteredReports = useMemo(() => {
        return reports
            .filter(r => {
                if (userSearch.trim()) {
                    if (!r.userEmail.toLowerCase().includes(userSearch.trim().toLowerCase())) return false;
                }
                if (reportSearch.trim()) {
                    if (!r.dataset.toLowerCase().includes(reportSearch.trim().toLowerCase())) return false;
                }
                if (fromDate && new Date(r.createdAt) < new Date(fromDate)) return false;
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    if (new Date(r.createdAt) > to) return false;
                }
                return true;
            });
    }, [reports, userSearch, reportSearch, fromDate, toDate]);



    // Pagination on flat filtered list
    const totalPages = Math.ceil(filteredReports.length / PAGE_SIZE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [fromDate, toDate, reportSearch, userSearch]);

    const paginatedReports = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredReports.slice(start, start + PAGE_SIZE);
    }, [filteredReports, currentPage]);

    const getHealthScoreColor = (score: number | null) => {
        if (score === null) return 'text-gray-400';
        if (score >= 80) return 'text-emerald-600';
        if (score >= 60) return 'text-amber-600';
        return 'text-brand-600';
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="space-y-3">
            {/* Filters Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-3 py-2.5">
                <div className="flex flex-wrap items-end gap-2">
                    {/* Registered Users Badge */}
                    <div className="flex items-center mr-1 mb-0.5 self-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            <VscAccount className="w-3 h-3 mr-1" />
                            {allUsers.length} Users
                        </span>
                        {reports.length > 0 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1.5">
                                <TbReportSearch className="w-3 h-3 mr-1" />
                                {reports.length} Reports
                            </span>
                        )}
                    </div>

                    {/* Filter by User Email */}
                    <div className="flex-[2] min-w-[240px]">
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">{t('Filter by User')}</label>
                        <div className="relative" ref={userDropdownRef}>
                            <VscSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 z-10" />
                            <input
                                type="text"
                                value={userSearch}
                                onChange={(e) => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                                onFocus={() => setShowUserDropdown(true)}
                                placeholder="Filter by user email..."
                                className="w-full pl-8 pr-7 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 focus:bg-white transition-all duration-200"
                            />
                            {userSearch && (
                                <button onClick={() => { setUserSearch(''); setShowUserDropdown(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10">
                                    <VscClose className="w-3 h-3" />
                                </button>
                            )}
                            {showUserDropdown && (
                                <div className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                                    {isLoadingUsers ? (
                                        <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                                            <VscLoading className="w-3.5 h-3.5 animate-spin" /> Loading users...
                                        </div>
                                    ) : filteredUsers.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-400">No users found</div>
                                    ) : (
                                        <>
                                            {userSearch && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setUserSearch(''); setShowUserDropdown(false); }}
                                                    className="w-full px-3 py-1.5 text-left text-xs text-brand-600 hover:bg-brand-50 border-b border-gray-100 font-medium"
                                                >
                                                    {t('Show all users')}
                                                </button>
                                            )}
                                            {filteredUsers.map(email => (
                                                <button
                                                    key={email}
                                                    type="button"
                                                    onClick={() => { setUserSearch(email); setShowUserDropdown(false); }}
                                                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 transition-colors flex items-center gap-2 ${userSearch === email ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700'
                                                        }`}
                                                >
                                                    <VscAccount className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                                    <span className="truncate">{email}</span>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Search by Dataset */}
                    <div className="flex-1 min-w-[160px]">
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">{t('Search Dataset')}</label>
                        <div className="relative">
                            <VscSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={reportSearch}
                                onChange={(e) => setReportSearch(e.target.value)}
                                placeholder="Dataset name..."
                                className="w-full pl-8 pr-7 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 focus:bg-white transition-all duration-200"
                            />
                            {reportSearch && (
                                <button onClick={() => setReportSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <VscClose className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* From Date */}
                    <div className="min-w-[130px]">
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">{t('From')}</label>
                        <input
                            type="date"
                            value={fromDate}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full py-1.5 px-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 focus:bg-white transition-all duration-200"
                        />
                    </div>

                    {/* To Date */}
                    <div className="min-w-[130px]">
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">{t('To')}</label>
                        <input
                            type="date"
                            value={toDate}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full py-1.5 px-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 focus:bg-white transition-all duration-200"
                        />
                    </div>

                    {/* Clear All */}
                    {(userSearch || fromDate || toDate || reportSearch) && (
                        <button
                            onClick={() => { setUserSearch(''); setFromDate(''); setToDate(''); setReportSearch(''); }}
                            className="py-1.5 px-2.5 text-[11px] font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md transition-colors flex items-center gap-1"
                        >
                            <VscClearAll className="w-3.5 h-3.5" />
                            {t('Clear')}
                        </button>
                    )}
                </div>
            </div>

            {/* Reports Table */}
            {isLoadingReports || isLoadingUsers ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <VscLoading className="w-7 h-7 text-brand-400 animate-spin" />
                    </div>
                    <p className="text-gray-500 font-medium">{t('Loading all reports...')}</p>
                    <p className="text-gray-400 text-sm mt-1">{t('Fetching reports for')} {allUsers.length} {t('users')}</p>
                </div>
            ) : filteredReports.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <TbReportSearch className="w-7 h-7 text-brand-300" />
                    </div>
                    <p className="text-gray-500 font-medium">{t('No reports found')}</p>
                    <p className="text-gray-400 text-sm mt-1">
                        {userSearch ? t('No reports matching') + ` "${userSearch}"` : t('No analyzed reports available')}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50">
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Dataset')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Health Score')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Issues')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Feedback')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Created')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('Actions')}</th>
                                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{t('User Email')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {paginatedReports.map((report, idx) => (
                                    <tr key={report.id} className={`hover:bg-brand-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                        <td className="px-5 py-3.5">
                                            <span className="text-sm font-medium text-gray-900">{report.dataset}</span>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {report.healthScore !== null ? (
                                                <span className={`text-sm font-semibold ${getHealthScoreColor(report.healthScore)}`}>
                                                    {report.healthScore}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {report.issueCount !== null ? (
                                                <span className="text-sm text-gray-700 font-medium">{report.issueCount}</span>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {feedbackMap[report.id] ? (
                                                <div className="flex flex-col gap-0.5">
                                                    {feedbackMap[report.id].rating !== null && (
                                                        <div className="flex items-center gap-0.5">
                                                            {[1, 2, 3, 4, 5].map(s => (
                                                                s <= (feedbackMap[report.id].rating ?? 0)
                                                                    ? <VscStarFull key={s} className="w-3 h-3 text-amber-500" />
                                                                    : <VscStarEmpty key={s} className="w-3 h-3 text-gray-300" />
                                                            ))}
                                                        </div>
                                                    )}
                                                    {feedbackMap[report.id].comment && (
                                                        <span className="text-xs text-gray-500 truncate max-w-[140px]" title={feedbackMap[report.id].comment!}>
                                                            {feedbackMap[report.id].comment}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <span className="text-xs text-gray-500">{formatDate(report.createdAt)}</span>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <button
                                                onClick={() => window.open(`/report?reportId=${report.id}&userEmail=${encodeURIComponent(report.userEmail)}`, '_blank')}
                                                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 text-white hover:opacity-90 shadow-sm"
                                                style={{ backgroundColor: '#D31145' }}
                                            >
                                                <VscEye className="w-3.5 h-3.5 mr-1" />
                                                {t('View')}
                                            </button>
                                        </td>
                                        <td className="px-5 py-3.5">
                                            <span className="text-sm text-gray-600">{report.userEmail}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
                            <span className="text-xs text-gray-500">
                                {t('Showing')} {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredReports.length)} {t('of')} {filteredReports.length} {t('reports')}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {t('Previous')}
                                </button>
                                <span className="px-2 text-xs text-gray-500">{t('Page')} {currentPage} {t('of')} {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {t('Next')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPanel;